import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FetchPolicy, gql } from '@apollo/client/core';
import { AppConfig } from '@app/core/app.config';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Work, WorkInput } from '@app/core/model/entities/works/work';
import { ActivationEndService } from '@app/features/main/activation-end.service';
import { AssetsService } from '@app/features/main/views/assets/assets.service';
import { SelectParams } from '@app/shared/grid/datasource-utils';
import ApiService from '@services/api.service';
import { FieldService } from '@services/field.service';
import { AppManager } from '@services/managers/app.manager';
import { plainToInstance } from 'class-transformer';
import { EMPTY, expand, from, Observable, Subject, switchMap } from 'rxjs';
import { map, reduce, tap, toArray } from 'rxjs/operators';

@Injectable({providedIn: 'root'})
export class WorksService {
  private sidePanelToggleSubject = new Subject<Work | null>();

  private addWorkSubject = new Subject<Work>();
  private updateWorkSubject = new Subject<Work>();
  private deleteWorksSubject = new Subject<Work[]>();

  private readonly apiService = inject(ApiService);
  private readonly appManager = inject(AppManager);
  private readonly appConfig = inject(AppConfig);
  private readonly router = inject(Router);
  private readonly activationEndService = inject(ActivationEndService);
  private readonly fieldService = inject(FieldService);
  private readonly assetsService = inject(AssetsService);

  private readonly workInfoGraphqlFragment = gql`
    fragment WorkInfo on Work {
      id
      identifier
      action
      state
      organizationId
      assetId
      spaceIdsList
      equipmentIdsList
      projectIdsList
      meetingId
      properties
      computedProperties
      creationActor {
        actorId
        name
      }
      creationDate
      lastChangeActor {
        actorId
        name
      }
      lastChangeDate
      dataDate
    }
  `;

  /**
   * Emits any new Work after it was created.
   */
  public get workAdded$(): Observable<Work> {
    return this.addWorkSubject.asObservable();
  }

  /**
   * Emits a Work whenever it has been updated.
   */
  public get workUpdated$(): Observable<Work> {
    return this.updateWorkSubject.asObservable();
  }

  /**
   * Emits a list of Works after they have been deleted.
   */
  public get worksDeleted$(): Observable<Work[]> {
    return this.deleteWorksSubject.asObservable();
  }

  /**
   * Emits data for the side panel to display or null whenever the side panel is closed.
   */
  public get sidePanelToggle$(): Observable<Work | null> {
    return this.sidePanelToggleSubject.asObservable();
  }

  /**
   * Loads a works page based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch works based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping, pagination.
   * It also fetches information about users who created and last updated the works.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, pagination, and locale settings.
   * @return An Observable emitting an array of Work objects, each with additional user information.
   */
  public loadWorksPage(selectParams: SelectParams): Observable<Work[]> {
    const query = gql`
      query Works($organizationId: String!, $selectParams: SelectParams!) {
        works(organizationId: $organizationId, selectParams: $selectParams) {
          ...WorkInfo
        }
      }
      ${this.workInfoGraphqlFragment}
    `;
    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      selectParams: selectParams
    };

    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(Work, data['works'] as Work[])));
  }

  /**
   * Loads all works pages based on the provided select parameters.
   *
   * This function performs a GraphQL query to fetch works based on the provided select parameters.
   * The select parameters include sorting, filtering, grouping.
   * It also fetches information about users who created and last updated the works.
   *
   * @param selectParams The parameters for the select query. These include sorting, filtering, grouping, and locale settings.
   * @return An Observable emitting an array of Work objects, each with additional user information.
   */
  public loadAllWorks(selectParams: SelectParams): Observable<Work[]> {
    const pageSize = this.appConfig.MAX_REQUEST_PAGE_SIZE;
    const params = {...selectParams, start: 0, count: pageSize};
    return this.loadWorksPage(params)
      .pipe(
        expand((works, i) => {
          if (works.length < pageSize) {
            return EMPTY;
          }
          params.start = (i + 1) * pageSize;
          return this.loadWorksPage(params);
        }),
        reduce((all, page) => {
          all.push(...page);
          return all;
        }, [] as Work[])
      );
  }

  /**
   * Loads work data based on the given work ID and fetch policy.
   * @param workId The identifier of the work to be fetched.
   * @param fetchPolicy The fetch policy for querying the data. Defaults to 'network-only'.
   * @return An observable containing the work data.
   */
  public loadWork(workId: string, fetchPolicy: FetchPolicy = 'network-only'): Observable<Work> {
    const query = gql`
      query Work($id: String!) {
        work(id: $id) {
          ...WorkInfo
        }
      }
      ${this.workInfoGraphqlFragment}
    `;
    const variables = {
      id: workId
    };

    return this.apiService.query({query, variables, fetchPolicy})
      .pipe(
        map(data => plainToInstance(Work, data['work'])),
        tap(() => {
          if (fetchPolicy === 'cache-first') {
            this.activationEndService.getRefreshHeaderSubject().next();
          }
        })
      );
  }

  /**
   * Get the field config for Work state and return the existing state values within the current Organization.
   * @return List of available states.
   */
  public loadWorkStates(): Observable<string[]> {
    return this.fieldService.getField('state', EntityTypeEnum.WORK)
      .pipe(map(field => field.fieldValues));
  }


  /**
   * Return whether changing a work's asset is allowed. This does not actually change the asset,
   * use {@link updateWorks} to update the Work after checking using this method.
   * @param workId ID of the Work to check.
   * @param assetId ID of the intended new related asset.
   * @return Null if move is allowed, error returned otherwise.
   */
  public canChangeWorkAsset(workId: string, assetId: string): Observable<Error | null> {
    const query = gql`
      query CanChangeWorkAsset($workId: String!, $assetId: String!) {
        canChangeWorkAsset(workId: $workId, assetId: $assetId)
      }
    `;
    const variables = {workId, assetId};

    return this.apiService.query({query, variables})
      .pipe(
        map(data => {
          const error = data['canChangeWorkAsset'] as string;
          return error ? new Error(error) : null;
        })
      );
  }

  /**
   * Make an API request to create a new Work with the provided data.
   * @param workInput Work data.
   * @return Observable emitting the new Work that was created.
   */
  public createWork(workInput: WorkInput): Observable<Work> {
    const mutation = gql`
      mutation CreateWork($workInput: CreateWorkInput!) {
        createWork(workInput: $workInput) {
          ...WorkInfo
        }
      }
      ${this.workInfoGraphqlFragment}
    `;
    const variables = {
      workInput: {
        organizationId: this.appManager.currentOrganization.id,
        ...workInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Work, data['createWork'])),
        tap(newWork => this.addWorkSubject.next(newWork))
      );
  }

  /**
   * Make an API request to copy the Work into another Asset.
   * @param works Works to duplicate.
   * @param assetId Optional ID of Asset to copy the Work into.
   * @param spaceIds IDs of Spaces to link to new Work.
   * @param equipmentIds IDs of Equipments to link to new Work.
   * @param projectIds IDs of Projects to link to new Work.
   * @return Observable emitting the Work that was created.
   */
  public duplicateWorks(works: Work[],
                        assetId?: string,
                        spaceIds?: string[],
                        equipmentIds?: string[],
                        projectIds?: string[]): Observable<Work[]> {
    const mutation = gql`
      mutation DuplicateWorks(
        $workIds: [String!]!,
        $assetId: String, $spaceIds: [String!],
        $equipmentIds: [String!],
        $projectIds: [String!]
      ) {
        duplicateWorks(
          ids: $workIds,
          assetId: $assetId,
          spaceIds: $spaceIds,
          equipmentIds: $equipmentIds,
          projectIds: $projectIds
        ) {
          ...WorkInfo
        }
      }${this.workInfoGraphqlFragment}
    `;
    const variables = {workIds: works.map(work => work.id), assetId, spaceIds, equipmentIds, projectIds};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Work, data['duplicateWorks'] as Work[])),
        switchMap(works => from(works)),
        tap(newWork => this.addWorkSubject.next(newWork)),
        toArray()
      );
  }

  /**
   * Call the API to update a Work.
   * @param work Work to update.
   * @param workInput Data to update the Work with.
   * @return Observable emitting the updated Work once the update is completed.
   */
  public updateWork(work: Work, workInput: WorkInput): Observable<Work> {
    const mutation = gql`
      mutation UpdateWork($workInput: UpdateWorkInput!) {
        updateWork(workInput: $workInput) {
          ...WorkInfo
        }
      }
      ${this.workInfoGraphqlFragment}
    `;
    const variables = {
      workInput: {
        id: work.id,
        action: work.action,
        state: work.state,
        assetId: work.assetId,
        spaceIds: work.spaceIds,
        equipmentIds: work.equipmentIds,
        projectIds: work.projectIds,
        meetingId: work.meetingId,
        ...workInput
      }
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Work, data['updateWork'])),
        tap(updatedWork => this.updateWorkSubject.next(updatedWork))
      );
  }

  /**
   * Call the API to update Works.
   * @param works List of Work to update.
   * @return Observable emitting the updated Works once the update is completed.
   */
  public updateWorks(works: { work: Work, workInput: WorkInput }[]): Observable<Work[]> {
    const mutation = gql`
      mutation UpdateWorks($workInputs: [UpdateWorkInput!]!) {
        updateWorks(workInputs: $workInputs) {
          ...WorkInfo
        }
      }
      ${this.workInfoGraphqlFragment}
    `;
    const variables = {
      workInputs: works.map(({work, workInput}) => {
        return {
          id: work.id,
          action: work.action,
          state: work.state,
          assetId: work.assetId,
          meetingId: work.meetingId,
          spaceIds: work.spaceIds,
          equipmentIds: work.equipmentIds,
          projectIds: work.projectIds,
          ...workInput
        };
      })
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Work, data['updateWorks'] as Work[])),
        switchMap(works => from(works)),
        tap(updatedWork => this.updateWorkSubject.next(updatedWork)),
        toArray()
      );
  }

  /**
   * Call the API to add related projects to specified works.
   * @param workIds List of work IDs whose related projects should be updated.
   * @param projectIds List of project IDs to add to the related projects of the specified works.
   * @return Observable emitting the updated Works once the update is completed.
   */
  public addProjectToWorks(workIds: string[], projectIds: string[]): Observable<Work[]> {
    const mutation = gql`
      mutation AddProjectIdsToWorks($workIds: [String!]!, $projectIds: [String!]!) {
        addProjectsToWorks(workIds: $workIds, projectIds: $projectIds) {
          ...WorkInfo
        }
      }
      ${this.workInfoGraphqlFragment}
    `;
    const variables = {workIds, projectIds};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(Work, data['addProjectsToWorks'] as Work[])),
        // Fetch Users who created and last updated the Work
        switchMap(works => from(works)),
        tap(newWork => this.updateWorkSubject.next(newWork)),
        toArray()
      );
  }

  /**
   * Make an API request to delete Works.
   * @param works Works to delete.
   * @return True if all Works have been deleted successfully, false otherwise.
   */
  public deleteWorks(works: Work[]): Observable<boolean> {
    const mutation = gql`
      mutation DeleteWork($workIds: [String!]!) {
        deleteWorks(ids: $workIds)
      }
    `;
    const variables = {workIds: works.map(work => work.id)};

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => data['deleteWorks'] as boolean),
        tap(success => success && this.deleteWorksSubject.next(works))
      );
  }

  /**
   * Open the sidenav with information about the selected Work.
   * @param work Work to be displayed in the side panel.
   */
  public openWorkSidePanel(work: Work): void {
    this.sidePanelToggleSubject.next(work);
  }

  /**
   * Close the sidenav.
   */
  public closeWorkSidePanel(): void {
    this.sidePanelToggleSubject.next(null);
  }

  /**
   * Navigate to a Work's sheet.
   * @param workId ID of the Work to navigate to.
   * @return Router's promise
   */
  public async navigateToWorkSheet(workId: string): Promise<void> {
    await this.router.navigate([
      'organization',
      this.appManager.currentOrganization.id,
      'works',
      'work-sheet',
      workId
    ]);
  }

  /**
   * Navigate to an Asset's sheet's works tab.
   * @param assetId ID of the Asset to navigate to.
   * @return Router's promise
   */
  public async navigateToAssetSheet(assetId: string): Promise<void> {
    await this.assetsService.navigateToAssetSheet(assetId, 'works');
  }
}
